import { Cake, PartyPopper } from "lucide-react";

/**
 * Dias a partir de hoje até o aniversário mais próximo (passado ou futuro).
 *   0   = hoje
 *   +N  = faltam N dias
 *   -N  = passou há N dias
 *
 * Usa getUTC* porque Prisma armazena birthDate como UTC midnight; métodos
 * locais dariam o dia anterior em UTC-3 (Brasil).
 *
 * Compara contra 3 candidatos (ano passado/atual/próximo) e retorna o mais
 * próximo de zero — trata virada de ano corretamente (ex: hoje 02/01 +
 * aniversário 30/12 → -3, não +363).
 */
export function daysFromBirthday(
  birthDate: Date | string | null | undefined,
): number | null {
  if (!birthDate) return null;
  const d = new Date(birthDate);
  if (Number.isNaN(d.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const m = d.getUTCMonth();
  const day = d.getUTCDate();
  const y = today.getFullYear();

  const candidates = [
    new Date(y - 1, m, day),
    new Date(y, m, day),
    new Date(y + 1, m, day),
  ];

  let best = Number.POSITIVE_INFINITY;
  for (const c of candidates) {
    const diff = Math.round(
      (c.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );
    if (Math.abs(diff) < Math.abs(best)) best = diff;
  }
  return Number.isFinite(best) ? best : null;
}

/**
 * Chip de aniversário — janela de ±3 dias (próximos ou recém-passados).
 * Hoje destaca com gradient saturado + animação. Passado fica mais discreto.
 */
export function BirthdayBadge({
  birthDate,
  thresholdDays = 3,
}: {
  birthDate: Date | string | null | undefined;
  thresholdDays?: number;
}) {
  const days = daysFromBirthday(birthDate);
  if (days === null || Math.abs(days) > thresholdDays) return null;

  const isToday = days === 0;
  const isPast = days < 0;

  const label = isToday
    ? "Hoje é o aniversário!"
    : days === 1
      ? "Amanhã é o aniversário"
      : days === -1
        ? "Aniversário foi ontem"
        : days > 0
          ? `Faltam ${days} dias para o aniversário`
          : `Aniversário foi há ${Math.abs(days)} dias`;

  const Icon = isToday ? PartyPopper : Cake;

  const classes = isToday
    ? "inline-flex shrink-0 items-center gap-1 rounded-full border border-rose-300 bg-gradient-to-r from-rose-100 via-amber-100 to-rose-100 px-2 py-0.5 text-[10.5px] font-semibold text-rose-700 shadow-sm dark:border-rose-500/40 dark:from-rose-500/20 dark:via-amber-500/15 dark:to-rose-500/20 dark:text-rose-300"
    : isPast
      ? "inline-flex shrink-0 items-center gap-1 rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[10.5px] font-medium text-zinc-600 shadow-sm dark:border-border dark:bg-muted/40 dark:text-muted-foreground"
      : "inline-flex shrink-0 items-center gap-1 rounded-full border border-rose-200 bg-gradient-to-r from-rose-50 via-amber-50 to-rose-50 px-2 py-0.5 text-[10.5px] font-semibold text-rose-700 shadow-sm dark:border-rose-500/30 dark:from-rose-500/15 dark:via-amber-500/10 dark:to-rose-500/15 dark:text-rose-300";

  const iconClasses = isToday
    ? "h-3.5 w-3.5 animate-[wiggle_1.2s_ease-in-out_infinite] text-rose-500 dark:text-rose-400"
    : isPast
      ? "h-3.5 w-3.5 text-zinc-400 dark:text-muted-foreground"
      : "h-3.5 w-3.5 text-rose-500 dark:text-rose-400";

  return (
    <span className={classes} title={label}>
      <Icon aria-hidden className={iconClasses} />
      <span className="whitespace-nowrap">{label}</span>
    </span>
  );
}
